import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamsService } from '../services/teams.service';
import type {
  CreateTeamDto,
  UpdateTeamDto,
  TeamFilters,
  AddAgentsDto,
} from '../types/team';

export const teamKeys = {
  all: ['teams'] as const,
  lists: () => [...teamKeys.all, 'list'] as const,
  list: (filters?: TeamFilters) => [...teamKeys.lists(), filters ?? {}] as const,
  detail: (id: string) => [...teamKeys.all, 'detail', id] as const,
  stats: (id: string) => [...teamKeys.all, 'stats', id] as const,
  ticketStats: () => [...teamKeys.all, 'ticket-stats'] as const,
};

export function useTeams(filters?: TeamFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: teamKeys.list(filters),
    queryFn: () => teamsService.getTeams(filters),
    enabled: options?.enabled ?? true,
  });
}

export function useTeam(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? teamKeys.detail(id) : [...teamKeys.all, 'detail', 'null'],
    queryFn: () => teamsService.getTeam(id as string),
    enabled: !!id,
  });
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateTeamDto) => teamsService.createTeam(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamKeys.lists() });
    },
  });
}

export function useUpdateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateTeamDto }) =>
      teamsService.updateTeam(id, dto),
    onSuccess: (data) => {
      qc.setQueryData(teamKeys.detail(data.id), data);
      qc.invalidateQueries({ queryKey: teamKeys.lists() });
    },
  });
}

export function useDeleteTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => teamsService.deleteTeam(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamKeys.lists() });
    },
  });
}

export function useAddAgents() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: AddAgentsDto }) =>
      teamsService.addAgents(id, dto),
    onSuccess: (data) => {
      qc.setQueryData(teamKeys.detail(data.id), data);
      qc.invalidateQueries({ queryKey: teamKeys.lists() });
    },
  });
}

export function useRemoveAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, agentId }: { id: string; agentId: string }) =>
      teamsService.removeAgent(id, agentId),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: teamKeys.detail(id) });
      qc.invalidateQueries({ queryKey: teamKeys.lists() });
    },
  });
}

export function useTeamStats(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? teamKeys.stats(id) : [...teamKeys.all, 'stats', 'null'],
    queryFn: () => teamsService.getStats(id as string),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

/** Ticket counts for every team, for the team cards. Refreshed every minute. */
export function useTeamTicketStats() {
  return useQuery({
    queryKey: teamKeys.ticketStats(),
    queryFn: () => teamsService.getTicketStats(),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
}
